import './App.sass';
import L, { LatLng } from "leaflet";
import 'leaflet/dist/leaflet.css';
import { useState, useEffect, useCallback, useMemo, useRef, forwardRef, useImperativeHandle, type CSSProperties, Fragment } from "react";

import {
    LayerGroup,
    LayersControl,
    MapContainer,
    Marker,
    Rectangle,
    Polygon,
    Polyline,
    Circle,
    TileLayer,
    useMapEvent,
    useMap,
    Tooltip,
} from "react-leaflet";
import { useEventHandlers } from '@react-leaflet/core';
import marker from './components/map/marker.svg';
import markerAddress from './components/map/marker-address-icon.svg';
import markerChargingStation from './components/map/marker-charging-station-icon.svg';
import markerUniversity from './components/map/marker-university-icon.svg';
import markerSchool from './components/map/marker-school-icon.svg';
import markerFireStation from './components/map/marker-fire-station-icon.svg';
import markerFireExtinguisher from './components/map/marker-fire-extinguisher-icon.svg';
import markerBillboard from './components/map/marker-billboard-icon.svg';
import markerChildCareFacility from './components/map/marker-child-care-facility-icon.svg';
import markerHospital from './components/map/marker-hospital-icon.svg';
import markerPharmacy from './components/map/marker-pharmacy-icon.svg';
import markerRestaurant from './components/map/marker-restaurant-icon.svg';
import markerHotel from './components/map/marker-hotel-icon.svg';
import markerAirport from './components/map/marker-airport-icon.svg';
import markerPoliceStation from './components/map/marker-police-station-icon.svg';
import markerFreewayMaintenanceOffice from './components/map/marker-freeway-maintenance-office-icon.svg';
import markerParkingLot from './components/map/marker-parking-lot-icon.svg';
import markerBusStop from './components/map/marker-bus-stop-icon.svg';
import markerChurch from './components/map/marker-church-icon.svg';
import markerHairdresser from './components/map/marker-hairdresser-icon.svg';
import markerSupermarket from './components/map/marker-supermarket-icon.svg';
import { brandMarkerUrl } from './components/map/brandMarkers';
import markerDoctor from './components/map/marker-doctor-icon.svg';
import markerParcelStation from './components/map/marker-parcel-station-icon.svg';
import markerCareFacility from './components/map/marker-care-facility-icon.svg';
import markerCompany from './components/map/marker-company-icon.svg';
import markerDepartment from './components/map/marker-department-icon.svg';
import markerCandidateProject from './components/map/marker-candidate-project-icon.svg';
import markerTrainStation from './components/map/marker-train-station-icon.svg';
import markerPowerUnit from './components/map/marker-power-unit-icon.svg';
import markerCellularTower from './components/map/marker-cellular-tower-icon.svg';
import markerDataCenter from './components/map/marker-data-center-icon.svg';
import markerMotorwayLink from './components/map/marker-motorway-link-icon.svg';
import markerAdvertisementColumn from './components/map/marker-advertisement-column.svg';
import markerPollution from './components/map/marker-pollution-icon.svg';
import markerBiomassUnit from './components/map/marker-biomassUnit-icon.svg';
import markerGasConsumerUnit from './components/map/marker-gasConsumerUnit-icon.svg';
import markerGasProducerUnit from './components/map/marker-gasProducerUnit-icon.svg';
import markerGasStorageUnit from './components/map/marker-gasStorageUnit-icon.svg';
import markerGeoGrubengasPressureReliefUnit from './components/map/marker-geoGrubengasPressureReliefUnit-icon.svg';
import markerNuclearUnit from './components/map/marker-nuclearUnit-icon.svg';
import markerSolarUnit from './components/map/marker-solarUnit-icon.svg';
import markerSubstation from './components/map/marker-substation-icon.svg';
import markerCombustionUnit from './components/map/marker-combustionUnit-icon.svg';
import markerElectricityStorageUnit from './components/map/marker-electricityStorageUnit-icon.svg';
import markerWaterUnit from './components/map/marker-waterUnit-icon.svg';
import markerWindUnit from './components/map/marker-windUnit-icon.svg';
import markerPlant from './components/map/marker-plant-icon.svg';
import markerLine from './components/map/marker-line-icon.svg';
import electricityConsumerUnit from './components/map/marker-electricityConsumerUnit-icon.svg';
import tinycolor from "tinycolor2";
import { useTranslation } from 'react-i18next';
import { userAPI } from './UserAPI';
import { AdvertisementColumn, Billboard, ChargingStation, Parcel, PoI, PoIType, University, getEntityType } from '@the-real-insight/tri-model';
import ClusterWithSuperCluster from './map/ClusterWithSuperCluster';
import { MapLayers, MapLayerElement } from './types/MapLayers';
import { useAtom } from 'jotai';
import { selectedEntityAtom } from './State';


// CRITICAL: Prevent leaflet-image crash with L.divIcon markers
// leaflet-image tries to access marker._icon.src which is undefined for divIcons,
// and then calls .match() on it, causing a crash.
const originalMarkerOnAdd = L.Marker.prototype.onAdd;
L.Marker.prototype.onAdd = function (this: L.Marker, map: L.Map) {
    const result = originalMarkerOnAdd.call(this, map);
    if (this.getElement()) {
        const el = this.getElement() as any;
        if (el && el.tagName === 'DIV' && !el.src) {
            // Set a dummy src so leaflet-image doesn't crash
            el.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
        }
    }
    return result;
};

// Fix Leaflet default icon paths for react-leaflet compatibility
// This is required because Leaflet's default icon paths don't work with bundlers
// Since we use custom icons, we can disable the default icon requirement
try {
    delete (L.Icon.Default.prototype as any)._getIconUrl;
    // Set default icon to use our custom marker as fallback
    L.Icon.Default.mergeOptions({
        iconRetinaUrl: marker,
        iconUrl: marker,
        shadowUrl: '',
        iconSize: [16, 22],
        iconAnchor: [8, 22],
        popupAnchor: [0, -22],
    });
} catch (error) {
    console.warn('Failed to configure Leaflet default icon:', error);
}

interface Properties {
    height?: number | string;
    setObject?: any;
    showMiniMap?: boolean;
    objectClass?: string;
    query: string | null;
    data?: any;
    options?: any;
    pois?: any[];
    layers?: MapLayers;
    hideOverlay?: boolean;
    /**
     * For a map embedded in a scrollable page (the conversation): gate zoom behind Ctrl/⌘ so a
     * plain wheel scrolls the page past the map instead of being swallowed by it. Off by default,
     * so the standalone map view keeps zooming directly on wheel.
     */
    requireModifierForScrollZoom?: boolean;
}

// Overlay shown for a moment when a plain wheel is handed on to the page instead of zooming.
// Sits above Leaflet's panes (z-index 400) and its controls (700-1000), and never eats a click.
const ZOOM_HINT_STYLE: CSSProperties = {
    position: 'absolute',
    inset: 0,
    zIndex: 1100,
    pointerEvents: 'none',
    background: 'rgba(30, 30, 30, 0.35)',
};

// The map is 800px tall, so a centred label would sit off-screen exactly when the user is
// scrolling past a partly-visible map. Anchoring it to the pointer's y instead needs no knowledge
// of the scroll position: the pointer is by definition over the visible slice.
const zoomHintLabelStyle = (y: number): CSSProperties => ({
    position: 'absolute',
    top: y,
    left: 0,
    right: 0,
    transform: 'translateY(-50%)',
    textAlign: 'center',
    color: '#fff',
    font: '600 14px/1.4 system-ui, sans-serif',
    textShadow: '0 1px 3px rgba(0,0,0,0.6)',
});

/**
 * Names the modifier the way the platform does: ⌘ on Apple, Ctrl/Strg on Windows and Linux. The
 * gate accepts both on every platform, so this only picks the idiomatic one to show. iPadOS 13+
 * reports itself as "Macintosh", which lands on ⌘ — correct, and moot without a wheel anyway.
 */
const zoomHintLabel = (t: (key: string) => string) =>
    /Mac|iPhone|iPad/.test(navigator.userAgent)
        ? t('map.zoomHintMeta')
        : t('map.zoomHintCtrl');

const center = new LatLng(50.10557929585495, 8.696741299147176);

const POSITION_CLASSES: any = {
    bottomleft: 'leaflet-bottom leaflet-left',
    bottomright: 'leaflet-bottom leaflet-right',
    topleft: 'leaflet-top leaflet-left',
    topright: 'leaflet-top leaflet-right',
}
const BOUNDS_STYLE = { weight: 1 }

/**
 * Tooltip text of a display element: a string stays one line, an array becomes one line per entry.
 *
 * Deliberately generic. A caller that has several things to say about a shape — what it is, and
 * then the number the reader actually came for — should not have to squeeze them into one run-on
 * line, and the map should not need to know what those things mean. Which is why this takes lines,
 * not fields.
 */
const tooltipContent = (tooltip: any) =>
    Array.isArray(tooltip)
        ? tooltip.map((line: any, index: number) => <div key={`tooltipLine${index}`}>{line}</div>)
        : tooltip;

// Grey style tiles - using Mapbox Light style
const tileUrl = 'https://api.mapbox.com/styles/v1/mapbox/light-v10/tiles/256/{z}/{x}/{y}?access_token=pk.eyJ1IjoibWFyY2dpbGxlIiwiYSI6ImNsZWlvazNkYTAzaWYzb25yZjBmejB5NmMifQ.XF9Z60UyGqXLztoIjH1SIA';
// Alternative grey styles:

const SELECTION_STYLE = {
    color: 'blue',
    weight: 1,
    fillColor: 'blue',
    fillOpacity: 0.05,
    opacity: 1.0,
    lineCap: 'square',
    lineJoin: 'miter'
} as any;

export const getBoundsForEntity = (entity: any): L.LatLngBounds | null => {
    if (!entity) return null;

    // 1. Check for explicit bounding box or bounds
    if (entity.boundingBox) {
        return L.latLngBounds(entity.boundingBox);
    }
    if (entity.bounds) {
        return L.latLngBounds(entity.bounds);
    }

    // 2. Check for shapes/geometries (Standard GeoJSON)
    const geometry = entity.shape?.geometry || entity.shape || entity.geometry;
    if (geometry && geometry.type && geometry.coordinates) {
        const bounds = L.latLngBounds([]);
        const processCoords = (coords: any) => {
            if (Array.isArray(coords[0]) && typeof coords[0][0] === 'number') {
                // Point array [lon, lat]
                coords.forEach((p: any) => bounds.extend([p[1], p[0]]));
            } else if (Array.isArray(coords[0])) {
                // Nested array
                coords.forEach(processCoords);
            }
        };
        
        if (geometry.type === 'Point') {
            const [lon, lat] = geometry.coordinates;
            bounds.extend([lat, lon]);
        } else {
            processCoords(geometry.coordinates);
        }
        
        if (bounds.isValid()) return bounds;
    }

    // 3. Check for Circle
    if (entity.lat !== undefined && entity.lon !== undefined && entity.radius) {
        const center = L.latLng(entity.lat, entity.lon);
        // Approximate bounds for a circle: extend by radius in degrees (very rough)
        // Better: use L.circle(center, radius).getBounds()
        return L.circle(center, { radius: entity.radius }).getBounds();
    }

    // 4. Check for legacy shapes (nested arrays of [lat, lon])
    if (Array.isArray(entity.shape) && Array.isArray(entity.shape[0])) {
        const bounds = L.latLngBounds([]);
        const processLegacy = (coords: any) => {
            if (typeof coords[0] === 'number') {
                bounds.extend(coords as [number, number]);
            } else {
                coords.forEach(processLegacy);
            }
        };
        processLegacy(entity.shape);
        if (bounds.isValid()) return bounds;
    }

    // 5. Fallback: single point (PoI, Bridge, etc.)
    if (entity.lat !== undefined && entity.lon !== undefined) {
        return L.latLngBounds([[entity.lat, entity.lon], [entity.lat, entity.lon]]);
    }

    return null;
};

// Add global styles to fix Leaflet defaults
if (typeof document !== 'undefined') {
    const styleId = 'map-selection-styles';
    if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.innerHTML = `
            .selected-marker-icon {
                background: transparent !important;
                border: none !important;
            }
            .leaflet-interactive:focus {
                outline: none !important;
            }
            path.leaflet-interactive {
                transition: none !important;
            }
        `;
        document.head.appendChild(style);
    }
}

const getStyle = (options: any, object: any, heatmapMax: number, selected: boolean, border: boolean = true) => {
    if (options?.heatmapField) {
        return {
            color: border ? 'black' : undefined,
            fillColor: options.heatmapField ? percentToColor(object[options.heatmapField] / heatmapMax * 100).toString() : '#649aaa',
            weight: 1,
            fill: true,
            fillOpacity: 0.3,
        }
    } else if (options && options.categoryField) {
        const entry = options.categoryField.values.find((entry: any) => entry.key === object[options.categoryField.field]);

        return {
            color: entry ? entry.color : '#649aaa',
            fillColor: entry ? entry.color : '#649aaa',
            weight: 1,
            fill: true,
            fillOpacity: 0.3,
        }
    } else {
        return {
            color: '#649aaa',
            fillColor: '#649aaa',
            weight: 1,
            fill: true,
            fillOpacity: 0.3,
        }
    }
};

const setStyle = (e: any, style: any) => {
    let layer = e.target;

    layer.setStyle(style);
};
const percentToColor = (value: number) => {
    const minimumColor = tinycolor('#00FF00');
    const maximumColor = tinycolor('#FF0000');
    const step = (maximumColor.toHsl().h - minimumColor.toHsl().h) / 100;

    return minimumColor.spin(step * value);
};

function MinimapBounds({ parentMap, zoom }: { parentMap: any, zoom: any }) {
    const minimap = useMap()

    // Clicking a point on the minimap sets the parent's map center
    const onClick = useCallback(
        (e: any) => {
            parentMap.setView(e.latlng, parentMap.getZoom())
        },
        [parentMap],
    )

    useMapEvent('click', onClick)

    // Keep track of bounds in state to trigger renders

    const [bounds, setBounds] = useState(parentMap.getBounds())

    const onChange = useCallback(() => {
        setBounds(parentMap.getBounds())
        // Update the minimap's view to match the parent map's center and zoom
        minimap.setView(parentMap.getCenter(), zoom)
    }, [minimap, parentMap, zoom])

    // Listen to events on the parent map

    const handlers = useMemo(() => ({ move: onChange, zoom: onChange }), [onChange])

    // @ts-ignore
    useEventHandlers({ instance: parentMap }, handlers)

    return <Rectangle bounds={bounds} pathOptions={BOUNDS_STYLE} />
}

function MinimapControl({ position, zoom }: { position: any, zoom: any }) {
    const parentMap = useMap()
    const mapZoom = zoom || 0

    // Memoize the minimap so it's not affected by position changes
    const minimap = useMemo(
        () => (
            <MapContainer
                style={{ height: 200, width: 200 }}
                center={parentMap.getCenter()}
                zoom={mapZoom}
                dragging={false}
                doubleClickZoom={true}
                scrollWheelZoom={false}
                attributionControl={false}
                zoomControl={false}>
                <TileLayer 
                    url={tileUrl}
                    tileSize={256}
                    zoomOffset={0}
                />
                <MinimapBounds parentMap={parentMap} zoom={mapZoom} />
            </MapContainer>
        ),
        [mapZoom, parentMap],
    )

    const positionClass =
        (position && POSITION_CLASSES[position]) || POSITION_CLASSES.topright
    return (
        <div className={positionClass}>
            <div className="leaflet-control leaflet-bar">{minimap}</div>
        </div>
    )
}

/**
 * Wheel gate for a map embedded in a scrollable page (the conversation).
 *
 * A map that swallows the wheel traps the reader: scrolling down over it zooms instead of moving
 * on through the chat. So a plain wheel is left to the page, and zoom is gated behind Ctrl/⌘ —
 * which is also what browsers report for a trackpad pinch, so pinch-to-zoom keeps working as is.
 * Dragging to pan is untouched.
 *
 * The listener sits on the map container's PARENT in the capture phase so it can flip Leaflet's
 * own scrollWheelZoom handler *before* the event reaches the container. Doing this from a listener
 * on the container itself would not work: a listener added while that same element is being
 * dispatched does not receive the current event, so the first zoom would always be swallowed.
 * Leaving the zoom to Leaflet's handler (rather than zooming by hand) keeps its tuned wheel
 * accumulation, and its preventDefault is what stops the browser's own ctrl+wheel page zoom.
 */
function ScrollZoomGate({ onBlockedWheel }: { onBlockedWheel: (pointerY: number) => void }) {
    const map = useMap();

    useEffect(() => {
        const host = map.getContainer().parentElement;
        if (!host) return;

        map.scrollWheelZoom.disable();
        const onWheel = (e: WheelEvent) => {
            if (e.ctrlKey || e.metaKey) {
                map.scrollWheelZoom.enable();
            } else {
                map.scrollWheelZoom.disable();
                onBlockedWheel(e.clientY - host.getBoundingClientRect().top);
            }
        };
        host.addEventListener('wheel', onWheel, { capture: true, passive: true });

        return () => {
            host.removeEventListener('wheel', onWheel, { capture: true });
            map.scrollWheelZoom.enable();
        };
    }, [map, onBlockedWheel]);

    return null;
}

// Component to ensure map size is properly invalidated when container becomes visible
function MapSizeHandler() {
    const map = useMap();
    
    useEffect(() => {
        // Invalidate size when component mounts
        const timeoutId = setTimeout(() => {
            try {
                map.invalidateSize();
            } catch (error) {
                console.error('Error invalidating map size:', error);
            }
        }, 100);
        
        // Also invalidate on window resize
        const handleResize = () => {
            try {
                map.invalidateSize();
            } catch (error) {
                console.warn('Map: Error invalidating size on resize:', error);
            }
        };
        
        window.addEventListener('resize', handleResize);
        
        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener('resize', handleResize);
        };
    }, [map]);
    
    return null;
}

// Component to render a selection box (bounding box + 5px padding)
function SelectionHighlight({ entity }: { entity: any }) {
    const map = useMap();
    const [bounds, setSelectionBounds] = useState<L.LatLngBounds | null>(null);

    const updateSelection = useCallback(() => {
        if (!entity || !map) {
            setSelectionBounds(null);
            return;
        }

        const geoBounds = getBoundsForEntity(entity);
        if (!geoBounds) {
            setSelectionBounds(null);
            return;
        }

        try {
            // For single points (PoI, Bridge), we want a box around the icon
            const isPoint = geoBounds.getNorthEast().equals(geoBounds.getSouthWest());
            const padding = 5;
            
            if (isPoint) {
                const point = map.latLngToLayerPoint(geoBounds.getNorthEast());
                // Use standard icon size 35x35 as base for PoI padding if it's a point
                const iconSize = 35;
                const padding = 5;
                const halfSize = (iconSize / 2) + padding;
                const _boxHeight = iconSize + 2 * padding; // 45px
                
                // Shift down (19px)
                const verticalShift = 19; 
                
                const nw = map.layerPointToLatLng(point.add([-halfSize, -iconSize - padding + verticalShift]));
                const se = map.layerPointToLatLng(point.add([halfSize, padding + verticalShift]));
                setSelectionBounds(L.latLngBounds(nw, se));
            } else {
                // For shapes, add 5px padding to the bounding box
                const nw = map.latLngToLayerPoint(geoBounds.getNorthWest());
                const se = map.latLngToLayerPoint(geoBounds.getSouthEast());
                
                const paddedNw = map.layerPointToLatLng(nw.add([-padding, -padding]));
                const paddedSe = map.layerPointToLatLng(se.add([padding, padding]));
                
                setSelectionBounds(L.latLngBounds(paddedNw, paddedSe));
            }
        } catch (e) {
            setSelectionBounds(geoBounds);
        }
    }, [entity, map]);

    useEffect(() => {
        updateSelection();
        map.on('zoomend moveend', updateSelection);
        return () => {
            map.off('zoomend moveend', updateSelection);
        };
    }, [map, updateSelection]);

    if (!bounds) return null;

    return (
        <Rectangle 
            bounds={bounds} 
            pathOptions={SELECTION_STYLE} 
            interactive={false}
        />
    );
}

// Component to handle bounds updates - react-leaflet MapContainer doesn't update bounds dynamically
function BoundsHandler({ bounds }: { bounds: any }) {
    const map = useMap();
    
    useEffect(() => {
        if (!map || !bounds || !bounds[0] || !bounds[1]) return;
        
        try {
            // Convert bounds to Leaflet LatLngBounds format
            const leafletBounds = L.latLngBounds(
                [bounds[0][0], bounds[0][1]],
                [bounds[1][0], bounds[1][1]]
            );
            
            // Fit bounds with padding and maxZoom to prevent excessive zoom
            map.fitBounds(leafletBounds, {
                padding: [50, 50],
                maxZoom: 18
            });
        } catch (error) {
            console.error('Error fitting bounds:', error);
        }
    }, [map, bounds]);
    
    return null;
}

// Component to handle tile layer events and ensure proper loading
function TileLayerHandler() {
    const map = useMap();
    
    useEffect(() => {
        // Wait for map to be ready
        const setupTileLayer = () => {
            try {
                // Ensure map size is valid
                map.invalidateSize();
                
                // Find the tile layer and ensure it loads
                map.eachLayer((layer: any) => {
                    if (layer instanceof L.TileLayer) {
                        // Force tile layer to load
                        layer.redraw();
                    }
                });
            } catch (error) {
                // Silently handle errors
            }
        };
        
        // Wait for map to be fully initialized
        if (map) {
            map.whenReady(() => {
                setTimeout(() => {
                    setupTileLayer();
                }, 200);
            });
        }
    }, [map]);
    
    return null;
}

// Component to force-enable touch interactions on the map instance
function TouchHandler() {
    const map = useMap();
    useEffect(() => {
        if (map) {
            // Wait for map to be fully ready before forcing handlers
            map.whenReady(() => {
                // Force enable touch zoom and dragging on the underlying Leaflet instance
                map.touchZoom.enable();
                map.dragging.enable();
                if (map.doubleClickZoom) map.doubleClickZoom.enable();
                if (map.boxZoom) map.boxZoom.enable();
                
                // DISABLING the 'tap' handler is the standard fix for pinch-zoom issues 
                // in modern Leaflet (1.x) on mobile browsers (especially Safari/iOS).
                // It prevents Leaflet from trying to "simulate" taps and lets the browser
                // pass multi-touch events through correctly.
                if ((map as any).tap) {
                    (map as any).tap.disable();
                }

                // Ensure the container has the correct touch-action
                const container = map.getContainer();
                if (container) {
                    container.style.touchAction = 'none';
                }
            });
        }
    }, [map]);
    return null;
}

const Map = forwardRef<any, Properties>((properties, ref) => {
    const { t } = useTranslation();
    // All hooks must be called unconditionally first
    const [map, setMap] = useState(null as any);

    // Transient "use ⌘/Strg to zoom" hint, shown when a plain wheel is passed through to the page.
    // The ref gates the state writes: a wheel gesture fires dozens of events, and only the first
    // one should re-render (and fix where the label goes) — the rest just extend the timer.
    const [showZoomHint, setShowZoomHint] = useState(false);
    const [zoomHintY, setZoomHintY] = useState(0);
    const zoomHintVisible = useRef(false);
    const zoomHintTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const flashZoomHint = useCallback((y: number) => {
        if (!zoomHintVisible.current) {
            zoomHintVisible.current = true;
            setZoomHintY(y);
            setShowZoomHint(true);
        }
        if (zoomHintTimer.current) clearTimeout(zoomHintTimer.current);
        zoomHintTimer.current = setTimeout(() => {
            zoomHintVisible.current = false;
            setShowZoomHint(false);
        }, 1200);
    }, []);
    useEffect(() => () => {
        if (zoomHintTimer.current) clearTimeout(zoomHintTimer.current);
    }, []);

    const [bounds, setBounds] = useState<any>(undefined);
    const [bridges, setBridges] = useState([]);
    const [buildings, setBuildings] = useState([]);
    const [areas, setAreas] = useState([]);
    const [shapes, setShapes] = useState([]);
    const [parcels, setParcels] = useState([]);
    const [circles, setCircles] = useState([]);
    const [options] = useState(properties?.options || {});
    const [heatmapMax, setHeatmapMax] = useState(100);
    const [_heatmapMin, setHeatmapMin] = useState(0);
    const [pieChartData, setPieChartData] = useState() as any;
    const [selectedEntity, setSelectedEntity] = useAtom(selectedEntityAtom) as any;

    // Expose map instance to parent component
    useImperativeHandle(ref, () => ({
        leafletElement: map,
        getMap: () => map
    }), [map]);

    // The data-loading logic intentionally keys off the incoming payload props.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const f = async () => {
            // console.log('Map Properties >>>', properties);

            // Handle new structured layers if provided
            const layers = (properties.layers || properties.data?.layers) as MapLayers;
            const hasNewLayers = !!layers;

            if (layers) {
                // Adjust bounds for all elements in all layers
                let allElements: MapLayerElement[] = [];
                Object.values(layers).forEach((layer: any) => {
                    if (layer?.elements) {
                        allElements = [...allElements, ...layer.elements];
                    }
                });
                
                // Only frame the map from its contents when the caller has not said where to
                // look. With `options.bounds` given, every change of layer — a metric switched on,
                // a parcel layer added — would otherwise re-frame around the new set and undo the
                // reader's own pan and zoom. An explicit frame is an instruction, not a suggestion.
                if (allElements.length > 0 && !properties.options?.bounds) {
                    adjustBoundsForLayerElements(allElements);
                }
            }

            if (!hasNewLayers && properties.objectClass === 'bridge') {
                const newBridges = await userAPI.getBridges(properties.query);

                setBridges(newBridges);

                newBridges.forEach((bridge: any) => {
                    bridge.class = 'Bridge';
                });
            } 
            
            // Process buildings if objectClass is 'building' OR if buildings data exists
            if (!hasNewLayers && (properties.objectClass === 'building' || properties.data?.buildings)) {
                let newBuildings;

                newBuildings = properties.data?.buildings || [];

                setBuildings(newBuildings);

                newBuildings.forEach((building: any) => {
                    building.class = 'Building';
                });

                calculateHeatmapDetails(newBuildings);
                adjustBoundsForFeatures(newBuildings);
            }
            
            if (!hasNewLayers && properties.data?.areas) {
                let newAreas;

                newAreas = properties.data?.areas || [];

                // TODO Hack

                newAreas.forEach((area: any) => {
                    area.class = 'Tile';
                });

                setAreas(newAreas);
                calculateHeatmapDetails(newAreas);
                adjustBoundsForFeatures(newAreas);
            } else if (!hasNewLayers && properties.data?.localGovernments) {
                let newLocalGovernments;
                newLocalGovernments = properties.data.localGovernments;

                // TODO Hack

                newLocalGovernments.forEach((area: any) => {
                    area.class = 'LocalGovernment';
                });

                setShapes(newLocalGovernments);
                adjustBoundsForFeatures(newLocalGovernments);
            } 
            
            if (!hasNewLayers && properties.data?.parcels) {
                let newParcels;
                newParcels = properties.data.parcels.map((parcel: any) => new Parcel(parcel));

                // TODO Hacks - Still needed???

                newParcels.forEach((parcel: any) => {
                    parcel.class = 'Parcel';
                });

                // console.log('Parcels >>>', newParcels);

                setParcels(newParcels);
                calculateHeatmapDetails(newParcels);
                adjustBoundsForFeatures(newParcels);
            } else if (!hasNewLayers && properties.objectClass === 'PostalCode' && properties.data?.postalCodes) {
                let newPostalCodes;
                newPostalCodes = properties.data.postalCodes;

                // TODO Hack

                newPostalCodes.forEach((postalCode: any) => {
                    postalCode.class = 'PostalCode';
                });

                setShapes(newPostalCodes);
                adjustBoundsForFeatures(newPostalCodes);
            } else if (!hasNewLayers && properties.data?.points && properties.options?.pieChart) {
                setPieChartData(properties.data.points);
                adjustBoundsForPoints();
            } 
            
            if (!hasNewLayers && properties.data?.shapes) {
                setShapes(properties.data.shapes);
                adjustBoundsForFeatures(properties.data.shapes);
            } else if (!hasNewLayers && properties.data?.circles) {
                let newCircles = properties.data.circles;
                
                newCircles.forEach((circle: any) => {
                    circle.class = 'Circle';
                });
                
                setCircles(newCircles);
                adjustBoundsForCircles(newCircles);
            }
        };

        f().catch((error) => console.error(error))
    }, [properties?.data, properties?.objectClass, properties?.options, properties?.pois]); // eslint-disable-line react-hooks/exhaustive-deps

    // Set bounds from options if available
    useEffect(() => {
        if (properties.options?.bounds && Array.isArray(properties.options.bounds) && 
            properties.options.bounds.length === 2 && 
            Array.isArray(properties.options.bounds[0]) && 
            Array.isArray(properties.options.bounds[1])) {
            
            setBounds(properties.options.bounds);
        }
    }, [properties.options?.bounds]);

    // Note: fitBounds is handled by MapContainer's bounds prop, but we keep this as a fallback
    // for cases where bounds are calculated from data after map initialization
    useEffect(() => {
        try {
            // Only fitBounds if bounds are set from data (not from options, which MapContainer handles)
            if (map && bounds && !properties.options?.bounds && 
                bounds[0] && bounds[1] && bounds[0][0] && bounds[0][1] && bounds[1][0] && bounds[1][1]) {
                // Convert bounds to Leaflet LatLngBounds format
                const leafletBounds = L.latLngBounds(
                    [bounds[0][0], bounds[0][1]],
                    [bounds[1][0], bounds[1][1]]
                );
                // Add padding and maxZoom to prevent excessive zoom
                map.fitBounds(leafletBounds, {
                    padding: [50, 50],
                    maxZoom: 18
                });
            }
        } catch (error) {
            console.error('Map: Error in fitBounds:', error);
        }
    }, [map, bounds, properties.options?.bounds]);

    // Invalidate map size when container becomes visible or dimensions change
    useEffect(() => {
        if (map) {
            // Use a small delay to ensure DOM is ready
            const timeoutId = setTimeout(() => {
                try {
                    map.invalidateSize();
                    // Force tile layer to reload after size invalidation
                    map.eachLayer((layer: any) => {
                        if (layer instanceof L.TileLayer) {
                            layer.redraw();
                        }
                    });
                } catch (error) {
                    console.warn('Map: Error invalidating size:', error);
                }
            }, 100);
            
            return () => clearTimeout(timeoutId);
        }
    }, [map, properties.height]);

    // Now we can do null checks after all hooks
    if (!properties) {
        console.error("[Map] properties is null or undefined");
        return <div>Loading...</div>;
    }
    
    if (typeof properties !== "object") {
        console.error("[Map] properties is not an object:", typeof properties, properties);
        return <div>Loading...</div>;
    }
    
    // Ensure properties.options exists and is an object
    if (!properties.options) {
        console.warn("[Map] properties.options is null/undefined, using empty object");
        properties.options = {};
    }
    
    if (typeof properties.options !== "object") {
        console.warn("[Map] properties.options is not an object, using empty object");
        properties.options = {};
    }

    const calculateHeatmapDetails = (features: any) => {
        if (!options?.heatmapField || !features || features.length === 0) {
            return;
        }

        let minimum = Number.MAX_VALUE;
        let maximum = Number.MIN_VALUE;

        features.forEach((feature: any) => {
            const value = feature[options?.heatmapField];

            if (value !== undefined && value !== null && !isNaN(value)) {
                minimum = Math.min(minimum, value);
                maximum = Math.max(maximum, value);
            }
        });

        // Only update if we found valid values
        if (minimum !== Number.MAX_VALUE && maximum !== Number.MIN_VALUE) {
            // console.log('Setting heatmap values - Min:', minimum, 'Max:', maximum);
            setHeatmapMin(minimum);
            setHeatmapMax(maximum);
        }
    }

    const adjustBoundsForFeatures = (features: any) => {
        let minimumLat = Number.MAX_VALUE;
        let maximumLat = Number.MIN_VALUE;
        let minimumLon = Number.MAX_VALUE;
        let maximumLon = Number.MIN_VALUE;

        features.forEach((feature: any) => {
            if (feature.boundingBox) {
                minimumLat = Math.min(minimumLat, feature.boundingBox[0][0]);
                maximumLat = Math.max(maximumLat, feature.boundingBox[1][0]);
                minimumLon = Math.min(minimumLon, feature.boundingBox[0][1]);
                maximumLon = Math.max(maximumLon, feature.boundingBox[1][1]);
            } else if (feature.shape) {
                feature.shape.forEach((entry: any) => {
                    if (Array.isArray(entry)) {
                        // Handle nested array structure: [[lat, lon], [lat, lon], ...]
                        entry.forEach((point: any) => {
                            if (Array.isArray(point) && point.length >= 2) {
                                minimumLat = Math.min(minimumLat, point[0]);
                                maximumLat = Math.max(maximumLat, point[0]);
                                minimumLon = Math.min(minimumLon, point[1]);
                                maximumLon = Math.max(maximumLon, point[1]);
                            }
                        });
                    } else if (entry.lat) {
                        // Handle object structure: {lat: number, lon: number}
                        minimumLat = Math.min(minimumLat, entry.lat);
                        maximumLat = Math.max(maximumLat, entry.lat);
                        minimumLon = Math.min(minimumLon, entry.lon);
                        maximumLon = Math.max(maximumLon, entry.lon);
                    }
                });
            } else if (feature.geometry) {
                // Handle GeoJSON geometry structure
                if (feature.geometry.coordinates && Array.isArray(feature.geometry.coordinates)) {
                    if (feature.geometry.type === 'Polygon' && feature.geometry.coordinates[0] && feature.geometry.coordinates[0][0]) {
                        // For Polygon geometry: coordinates[0][0] contains the outer ring coordinates
                        feature.geometry.coordinates[0][0].forEach((point: any) => {
                            if (Array.isArray(point) && point.length >= 2) {
                                // GeoJSON uses [lon, lat] order, but we need [lat, lon] for bounds
                                const lat = point[1];
                                const lon = point[0];
                                minimumLat = Math.min(minimumLat, lat);
                                maximumLat = Math.max(maximumLat, lat);
                                minimumLon = Math.min(minimumLon, lon);
                                maximumLon = Math.max(maximumLon, lon);
                            }
                        });
                    } else if (feature.geometry.type === 'MultiPolygon' && feature.geometry.coordinates[0]) {
                        // For MultiPolygon geometry: coordinates[0] contains array of polygons
                        // Each polygon is coordinates[0][i][0] for the outer ring
                        feature.geometry.coordinates[0].forEach((polygon: any, _polygonIndex: number) => {
                            if (polygon && polygon[0]) {
                                polygon.forEach((coordinateArray: any, _arrayIndex: number) => {
                                    if (coordinateArray && Array.isArray(coordinateArray)) {
                                        coordinateArray.forEach((point: any) => {
                                            if (Array.isArray(point) && point.length >= 2) {
                                                // GeoJSON uses [lon, lat] order
                                                const lon = point[0];
                                                const lat = point[1];
                                                if (typeof lon === 'number' && typeof lat === 'number' && 
                                                    !isNaN(lon) && !isNaN(lat)) {
                                                    minimumLat = Math.min(minimumLat, lat);
                                                    maximumLat = Math.max(maximumLat, lat);
                                                    minimumLon = Math.min(minimumLon, lon);
                                                    maximumLon = Math.max(maximumLon, lon);
                                                }
                                            }
                                        });
                                    }
                                });
                            }
                        });
                    } else {
                        // Handle other geometry types or fallback
                        feature.geometry.coordinates.forEach((entry: any) => {
                            if (Array.isArray(entry)) {
                                // Handle nested array structure: [[lat, lon], [lat, lon], ...]
                                entry.forEach((point: any) => {
                                    if (Array.isArray(point) && point.length >= 2) {
                                        minimumLat = Math.min(minimumLat, point[0]);
                                        maximumLat = Math.max(maximumLat, point[0]);
                                        minimumLon = Math.min(minimumLon, point[1]);
                                        maximumLon = Math.max(maximumLon, point[1]);
                                    }
                                });
                            } else if (entry.lat) {
                                // Handle object structure: {lat: number, lon: number}
                                minimumLat = Math.min(minimumLat, entry.lat);
                                maximumLat = Math.max(maximumLat, entry.lat);
                                minimumLon = Math.min(minimumLon, entry.lon);
                                maximumLon = Math.max(maximumLon, entry.lon);
                            }
                        });
                    }
                }
            }
        });

        // console.log('Minimum lat >>>', minimumLat);
        // console.log('Maximum lat >>>', maximumLat);
        // console.log('Minimum lon >>>', minimumLon);
        // console.log('Maximum lon >>>', maximumLon);

        // Only set bounds if we found valid coordinates
        if (minimumLat !== Number.MAX_VALUE && maximumLat !== Number.MIN_VALUE && 
            minimumLon !== Number.MAX_VALUE && maximumLon !== Number.MIN_VALUE) {
            setBounds([[minimumLat, minimumLon], [maximumLat, maximumLon]] as any);
        }
    }

    const adjustBoundsForPoints = () => {
        if (properties.data?.points && properties.data.points.length > 0) {
            let minimumLat = Number.MAX_VALUE;
            let maximumLat = Number.MIN_VALUE;
            let minimumLon = Number.MAX_VALUE;
            let maximumLon = Number.MIN_VALUE;

            properties.data.points.forEach((point: any) => {
                minimumLat = Math.min(minimumLat, point.lat);
                maximumLat = Math.max(maximumLat, point.lat);
                minimumLon = Math.min(minimumLon, point.lon);
                maximumLon = Math.max(maximumLon, point.lon);
            });

            setBounds([[minimumLat, minimumLon], [maximumLat, maximumLon]] as any);
        }
    }

    const adjustBoundsForCircles = (circles: any[]) => {
        if (circles && circles.length > 0) {
            let minimumLat = Number.MAX_VALUE;
            let maximumLat = Number.MIN_VALUE;
            let minimumLon = Number.MAX_VALUE;
            let maximumLon = Number.MIN_VALUE;

            circles.forEach((circle: any) => {
                if (circle.lat && circle.lon) {
                    // Calculate the radius in degrees (approximate conversion from km)
                    const radiusKm = circle.radius || circle.radiusKm || 1; // Default 1km if no radius specified
                    const radiusDegrees = radiusKm / 111; // Rough conversion: 1 degree ≈ 111 km
                    
                    minimumLat = Math.min(minimumLat, circle.lat - radiusDegrees);
                    maximumLat = Math.max(maximumLat, circle.lat + radiusDegrees);
                    minimumLon = Math.min(minimumLon, circle.lon - radiusDegrees);
                    maximumLon = Math.max(maximumLon, circle.lon + radiusDegrees);
                }
            });

            // Only set bounds if we found valid coordinates
            if (minimumLat !== Number.MAX_VALUE && maximumLat !== Number.MIN_VALUE && 
                minimumLon !== Number.MAX_VALUE && maximumLon !== Number.MIN_VALUE) {
                setBounds([[minimumLat, minimumLon], [maximumLat, maximumLon]] as any);
            }
        }
    }

    const adjustBoundsForLayerElements = (elements: MapLayerElement[]) => {
        let minimumLat = Number.MAX_VALUE;
        let maximumLat = Number.MIN_VALUE;
        let minimumLon = Number.MAX_VALUE;
        let maximumLon = Number.MIN_VALUE;

        elements.forEach((element) => {
            const display = { ...element.display };
            const entity = element.entity;
            
            // Fallback for display data from entity if missing
            if (entity) {
                if (!display.shape && (entity.shape || entity.geometry)) {
                    display.shape = entity.shape || entity.geometry;
                }
                if (display.lat === undefined || display.lon === undefined) {
                    if (entity.lat !== undefined && entity.lon !== undefined) {
                        display.lat = entity.lat;
                        display.lon = entity.lon;
                    } else {
                        const bounds = getBoundsForEntity(entity);
                        if (bounds) {
                            const center = bounds.getCenter();
                            display.lat = center.lat;
                            display.lon = center.lng;
                        }
                    }
                }
            }

            if (display.type === 'poi' || display.type === 'circle' || display.type === 'chart') {
                if (display.lat !== undefined && display.lon !== undefined) {
                    const lat = display.lat;
                    const lon = display.lon;
                    
                    if (display.type === 'circle' && display.radius) {
                        const radiusDegrees = display.radius / 111000; // meters to degrees approx
                        minimumLat = Math.min(minimumLat, lat - radiusDegrees);
                        maximumLat = Math.max(maximumLat, lat + radiusDegrees);
                        minimumLon = Math.min(minimumLon, lon - radiusDegrees);
                        maximumLon = Math.max(maximumLon, lon + radiusDegrees);
                    } else {
                        minimumLat = Math.min(minimumLat, lat);
                        maximumLat = Math.max(maximumLat, lat);
                        minimumLon = Math.min(minimumLon, lon);
                        maximumLon = Math.max(maximumLon, lon);
                    }
                }
            } else if ((display.type === 'shape' || display.type === 'way')) {
                // First use lat/lon if available (either explicitly or via fallback)
                if (display.lat !== undefined && display.lon !== undefined) {
                    minimumLat = Math.min(minimumLat, display.lat);
                    maximumLat = Math.max(maximumLat, display.lat);
                    minimumLon = Math.min(minimumLon, display.lon);
                    maximumLon = Math.max(maximumLon, display.lon);
                }
                
                // Then use shape if available
                if (display.shape) {
                    // Reuse existing logic for GeoJSON geometry
                    const geometry = display.shape.geometry || display.shape;
                    if (geometry.coordinates && Array.isArray(geometry.coordinates)) {
                        const processPoints = (points: any) => {
                            if (Array.isArray(points[0])) {
                                points.forEach((p: any) => processPoints(p));
                            } else if (points.length >= 2) {
                                const lon = points[0];
                                const lat = points[1];
                                if (typeof lat === 'number' && typeof lon === 'number') {
                                    minimumLat = Math.min(minimumLat, lat);
                                    maximumLat = Math.max(maximumLat, lat);
                                    minimumLon = Math.min(minimumLon, lon);
                                    maximumLon = Math.max(maximumLon, lon);
                                }
                            }
                        };
                        processPoints(geometry.coordinates);
                    }
                }
            }
        });

        if (minimumLat !== Number.MAX_VALUE && maximumLat !== Number.MIN_VALUE && 
            minimumLon !== Number.MAX_VALUE && maximumLon !== Number.MIN_VALUE) {
            setBounds([[minimumLat, minimumLon], [maximumLat, maximumLon]] as any);
        }
    }

    const getShapeColorForHeatmap = (shape: any) => {
        if (!properties.options?.heatmap?.categories) {
            return '#649aaa';
        }

        for (const category of properties.options?.heatmap?.categories || []) {
            if (shape[properties.options?.heatmap?.field] <= category.max && shape[properties.options?.heatmap?.field] >= category.min) {
                return category.color;
            }
        }

        return '#649aaa';
    }

    const poiTooltip = (poi: PoI) => {
        switch (poi.poiType) {
            case PoIType.chargingStation: return `Ladestation von ${(poi as ChargingStation).operator} mit ${(poi as ChargingStation).ratedPower} kW Nennleistung.`;
            case PoIType.university: return `${(poi as University).name}, gegründet ${(poi as University).foundationYear} mit ${(poi as University).numberOfStudents} Studierenden.`;
            case PoIType.advertisementColumn: return `Litfaßsäule, ${(poi as AdvertisementColumn).lit ? 'beleuchtet' : 'unbeleuchtet'}`;
            case PoIType.billboard: return `Anzeigetafel ${(poi as Billboard).operator || ''}, ${(poi as AdvertisementColumn).lit ? 'beleuchtet' : 'unbeleuchtet'}`;
            // Generic fallback so plain markers (e.g. geocoded addresses) show
            // their name instead of an empty tooltip.
            default: return (poi as any).name || undefined;
        }
    }

    const isEntitySelected = (entity: any) => {
        if (!selectedEntity || !entity) return false;
        if (selectedEntity === entity) return true;
        if (selectedEntity._id && entity._id && selectedEntity._id === entity._id) return true;
        if (selectedEntity.id && entity.id && selectedEntity.id === entity.id) return true;
        return false;
    };

    const poiMarker = (poi: PoI | any) => {
        const poiType = poi.poiType || poi.type || poi.class;
        const _isSelected = isEntitySelected(poi);

        const getIconUrl = (type: any) => {
            const t = String(type || '');
            const lowerT = t.toLowerCase();
            const cleanT = lowerT.replace(/[^a-z0-9]/g, '');
            
            // Try exact match first (enum value)
            if (t === PoIType.address || lowerT === 'address') return markerAddress;
            if (t === PoIType.chargingStation || lowerT === 'chargingpoint' || cleanT === 'chargingstation') return markerChargingStation;
            if (t === PoIType.university || lowerT === 'university') return markerUniversity;
            if (t === PoIType.school || lowerT === 'school') return markerSchool;
            if (t === PoIType.fireStation || cleanT === 'firestation') return markerFireStation;
            if (cleanT === 'fireextinguisher') return markerFireExtinguisher;
            if (t === PoIType.advertisementColumn || cleanT === 'advertisementcolumn' || lowerT === 'advertisement-column') return markerAdvertisementColumn;
            if (t === PoIType.billboard || lowerT === 'billboard') return markerBillboard;
            if (t === PoIType.childCareFacility || cleanT === 'childcarefacility' || lowerT === 'child-care-facility' || lowerT === 'kindergarten') return markerChildCareFacility;
            if (t === PoIType.hospital || lowerT === 'hospital') return markerHospital;
            if (t === PoIType.pharmacy || lowerT === 'pharmacy') return markerPharmacy;
            if (t === PoIType.hotel || lowerT === 'hotel') return markerHotel;
            if (t === PoIType.restaurant || lowerT === 'restaurant') return markerRestaurant;
            if (t === PoIType.airport || lowerT === 'airport') return markerAirport;
            if (t === PoIType.policeStation || cleanT === 'policestation') return markerPoliceStation;
            if (t === PoIType.freewayMaintenanceOffice || cleanT === 'freewaymaintenanceoffice') return markerFreewayMaintenanceOffice;
            if (t === PoIType.parkingLot || lowerT === 'parkinglot') return markerParkingLot;
            if (t === PoIType.busStop || lowerT === 'busstop') return markerBusStop;
            if (t === PoIType.church || lowerT === 'church') return markerChurch;
            if (t === PoIType.hairdresser || lowerT === 'hairdresser') return markerHairdresser;
            if (t === PoIType.supermarket || lowerT === 'supermarket') return markerSupermarket;
            if (t === PoIType.doctor || lowerT === 'doctor') return markerDoctor;
            if (t === PoIType.parcelStation || lowerT === 'parcelstation') return markerParcelStation;
            if (t === PoIType.careFacility || lowerT === 'carefacility') return markerCareFacility;
            if (t === PoIType.trainStation || lowerT === 'trainstation') return markerTrainStation;
            if (t === PoIType.powerUnit || lowerT === 'powerunit') return markerPowerUnit;
            if (t === PoIType.cellularTower || lowerT === 'cellulartower') return markerCellularTower;
            // Both spellings of the enum: `dataCenter` and the underscored `data_center`.
            if (cleanT === 'datacenter') return markerDataCenter;
            if (t === PoIType.motorwayLink || cleanT === 'motorwaylink') return markerMotorwayLink;
            if (cleanT === 'biomassunit') return markerBiomassUnit;
            if (cleanT === 'gasconsumerunit') return markerGasConsumerUnit;
            if (cleanT === 'gasproducerunit') return markerGasProducerUnit;
            if (cleanT === 'gasstorageunit') return markerGasStorageUnit;
            if (cleanT === 'geogrubengaspressurereliefunit') return markerGeoGrubengasPressureReliefUnit;
            if (cleanT === 'nuclearunit') return markerNuclearUnit;
            if (cleanT === 'solarunit') return markerSolarUnit;
            if (cleanT === 'substation') return markerSubstation;
            if (lowerT === 'department') return markerDepartment;
            if (cleanT === 'candidateproject') return markerCandidateProject;
            if (lowerT === 'company') return markerCompany;
            if (cleanT === 'pollutiondata') return markerPollution;
            if (cleanT === 'combustionunit') return markerCombustionUnit;
            if (cleanT === 'electricitystorageunit') return markerElectricityStorageUnit;
            if (cleanT === 'waterunit') return markerWaterUnit;
            if (cleanT === 'windunit') return markerWindUnit;
            if (cleanT === 'electricityconsumerunit') return electricityConsumerUnit;
            if (cleanT === 'plant') return markerPlant;
            if (cleanT === 'line') return markerLine;
            // If it's a known non-POI layer element type, return school as fallback (like MapAndImage)
            if (['shape', 'way', 'circle', 'chart'].includes(lowerT)) return markerSchool;

            return markerSchool; // Use school icon as absolute fallback instead of blue drop
        };

        const iconUrl = brandMarkerUrl(poi.brandWikidata) ?? getIconUrl(poiType);
        
        // Define specific border color based on poiType
        let borderColor = '#000';
        const cleanT = String(poiType || '').toLowerCase().replace(/[^a-z0-9]/g, '');
        switch (cleanT) {
                case 'chargingstation':
                case 'chargingpoint':
                case 'biomassunit':
                case 'gasconsumerunit':
                case 'gasproducerunit':
                case 'gasstorageunit':
                case 'geogrubengaspressurereliefunit':
                case 'solarunit':
                    borderColor = '#000'; break;
                case 'substation':
                case 'powerunit':
                    borderColor = '#000'; break;
                case 'nuclearunit':
                    borderColor = '#000'; break;
                case 'university':
            case 'school':
            case 'childcarefacility':
            case 'kindergarten':
            case 'pharmacy':
            case 'busstop':
            case 'church':
            case 'hairdresser':
            case 'doctor':
            case 'parcelstation':
            case 'trainstation':
            case 'cellulartower':
            case 'datacenter':
            case 'motorwaylink':
                borderColor = '#0000AA'; break;
            case 'hospital':
            case 'firestation':
                borderColor = '#AA0000'; break;
            case 'restaurant':
            case 'hotel':
            case 'supermarket':
                borderColor = '#ca03ca'; break;
            case 'billboard':
            case 'advertisementcolumn':
                borderColor = '#BBBBBB'; break;
            case 'department':
                borderColor = '#00695C'; break;
            case 'candidateproject':
                borderColor = '#C2185B'; break;
        }

        const coloured = typeof poi.markerColor === 'string' && /^#[0-9a-fA-F]{3,8}$/.test(poi.markerColor);

        // Use L.divIcon to render the drop pin structure from App.sass
        return L.divIcon({
            className: 'marker-div-icon',
            html: `
                <div class="marker" style="width: 35px; height: 35px;">
                    ${
                        // A score colour fills the whole pin (heatmap: a project's properties scored where
                        // they stand) — the type icon would cover it, so a white dot takes its place.
                        coloured
                            ? `<span class="pin" style="background: ${poi.markerColor}; border: 2px solid #ffffff; box-shadow: 0 0 0 1px rgba(0,0,0,0.45);"><span style="display: block; width: 10px; height: 10px; border-radius: 50%; background: #ffffff;"></span></span>`
                            : `<span class="pin ${String(poiType || '').replace(/[^a-zA-Z0-9]/g, '')}" style="border: 0.5px solid ${borderColor};"><img src="${iconUrl}" style="width: 25px; height: 25px;" /></span>`
                    }
                </div>
            `,
            iconSize: [35, 35],
            iconAnchor: [17.5, -1.29],
            popupAnchor: [0, -35]
        });
    };

    const renderLayerElement = (element: MapLayerElement, index: number, layerName: string) => {
        const { display } = element;
        let { entity } = element;
        const key = `${layerName}-${display.type}-${index}`;

        // Transform entity to proper type if _type is present
        if (entity && entity._type && typeof (entity as any).getTypeClass !== 'function') {
            try {
                const EntityClass = getEntityType(entity._type);
                if (EntityClass) {
                    // Generic normalization: Ensure both legacy 'shape' and GeoJSON 'geometry' are present if possible
                    if (!entity.shape && entity.geometry?.coordinates?.[0]) {
                        const coords = entity.geometry.type === 'Polygon' ? entity.geometry.coordinates[0] : entity.geometry.coordinates;
                        if (Array.isArray(coords)) {
                            entity.shape = coords.map((coord: any) => 
                                Array.isArray(coord) ? { lon: coord[0], lat: coord[1] } : coord
                            );
                        }
                    } else if (!entity.geometry && entity.shape && Array.isArray(entity.shape)) {
                        entity.geometry = {
                            type: 'Polygon', // Default to Polygon for shapes
                            coordinates: [entity.shape.map((point: any) => [point.lon || point.lng, point.lat])]
                        };
                    }

                    entity = new (EntityClass as any)(entity);
                }
            } catch (e) {
                console.warn(`Failed to instantiate entity of type ${entity._type}:`, e);
            }
        }
        
        // Use entity if present, otherwise create a virtual entity
        // We use the element itself as a stable reference if entity is missing
        const targetEntity = entity || (element as any)._virtualEntity || ({
            ...element,
            class: display.type.charAt(0).toUpperCase() + display.type.slice(1),
            _type: display.type.charAt(0).toUpperCase() + display.type.slice(1),
            name: display.name || display.tooltip || (display.type === 'way' ? 'Way' : (display.type === 'poi' ? 'PoI' : (display.type === 'circle' ? 'Circle' : 'Shape'))),
            getTypeName: () => display.type.charAt(0).toUpperCase() + display.type.slice(1)
        });
        
        // Store virtual entity on the element to keep it stable across renders
        if (!entity && !(element as any)._virtualEntity) {
            (element as any)._virtualEntity = targetEntity;
        }

        // Fallback for display data from entity if missing
        if (targetEntity) {
            if (!display.shape && (targetEntity.shape || targetEntity.geometry)) {
                display.shape = targetEntity.shape || targetEntity.geometry;
            }
            // A shape is positioned by its geometry, so it is not given a derived centre here. The
            // shape branch plants a marker wherever `lat`/`lon` exist, and filling them in from the
            // entity's own bounds meant every outline that carried an entity got a pin nobody asked
            // for — a field of parcels came out as a field of pins, each one drawn below its plot
            // because the pin icon anchors above its point. A caller that wants a marker on a shape
            // still gets one by setting lat/lon itself.
            const positionedByGeometry = display.type === 'shape' || display.type === 'way';

            if (!positionedByGeometry && (display.lat === undefined || display.lon === undefined)) {
                // Try to get lat/lon from entity
                if (targetEntity.lat !== undefined && targetEntity.lon !== undefined) {
                    display.lat = targetEntity.lat;
                    display.lon = targetEntity.lon;
                } else {
                    // Try to get center from shape/geometry
                    const bounds = getBoundsForEntity(targetEntity);
                    if (bounds) {
                        const center = bounds.getCenter();
                        display.lat = center.lat;
                        display.lon = center.lng;
                    }
                }
            }
        }

        const _isSelected = isEntitySelected(targetEntity);

        switch (display.type) {
            case 'shape':
            case 'way': {
                const geometry = display.shape?.geometry || display.shape;
                if (!geometry) return null;
                
                // Render the shape (Polygon/Polyline)
                const shapeElements: any[] = [];
                
                // Handle GeoJSON MultiPolygon or Polygon
                const renderPolygon = (coords: any, idx: number) => {
                    if (!Array.isArray(coords) || coords.length === 0) return null;

                    // Support both standard GeoJSON nested rings and flat array of points
                    const isFlat = Array.isArray(coords[0]) && typeof coords[0][0] === 'number';
                    const rings = isFlat ? [coords] : coords;

                    const latLngs = rings.map((ring: any) => {
                        if (!Array.isArray(ring)) return [];
                        return ring.map((coord: any) => {
                            if (Array.isArray(coord) && coord.length >= 2) {
                                return new LatLng(coord[1], coord[0]);
                            } else if (coord && typeof coord.lat === 'number' && typeof coord.lon === 'number') {
                                return new LatLng(coord.lat, coord.lon);
                            }
                            return null;
                        }).filter((c): c is LatLng => c !== null);
                    }).filter((ring: any[]) => ring.length >= 3);

                    if (latLngs.length === 0) return null;

                    const label = display.label && idx === 0 ? display.label : null;

                    return (
                        <Fragment key={`${key}-poly-${idx}`}>
                            <Polygon
                                positions={latLngs}
                                pathOptions={{
                                    color: display.color || '#649aaa',
                                    fillColor: display.fillColor || display.color || '#649aaa',
                                    fillOpacity: display.fillOpacity ?? 0.3,
                                    weight: display.weight ?? 1,
                                    opacity: display.opacity ?? 0.8,
                                }}
                                eventHandlers={{
                                    click: () => {
                                        if (properties.setObject) properties.setObject(targetEntity);
                                        if (!properties.hideOverlay) {
                                            setSelectedEntity(targetEntity);
                                        }
                                    }
                                }}
                            >
                                {display.tooltip ? (
                                    <Tooltip>{tooltipContent(display.tooltip)}</Tooltip>
                                ) : label ? (
                                    <Tooltip permanent direction="center" opacity={1} className="shapeLabel">
                                        {label}
                                    </Tooltip>
                                ) : null}
                            </Polygon>

                            {/* The written-in name needs a layer of its own where the shape also
                                answers on hover. Leaflet binds *one* tooltip per layer — `bindTooltip`
                                begins by unbinding what is there — so a permanent label and a hover
                                tooltip on the same polygon do not coexist: the second silently
                                replaces the first, and the parcel numbers vanish.

                                So the label rides on a copy of the outline that draws nothing and
                                takes no pointer, which leaves the hover to the polygon underneath. */}
                            {label && display.tooltip ? (
                                <Polygon
                                    positions={latLngs}
                                    interactive={false}
                                    pathOptions={{ opacity: 0, fillOpacity: 0, weight: 0 }}
                                >
                                    <Tooltip permanent direction="center" opacity={1} className="shapeLabel">
                                        {label}
                                    </Tooltip>
                                </Polygon>
                            ) : null}
                        </Fragment>
                    );
                };

                const renderPolyline = (coords: any, idx: number) => {
                    if (!Array.isArray(coords)) return null;

                    // Support both standard GeoJSON LineString and flat array of points
                    const isFlat = Array.isArray(coords[0]) && typeof coords[0][0] === 'number';
                    const lines = isFlat ? [coords] : (Array.isArray(coords[0]) ? coords : [coords]);

                    const allLinesLatLngs = lines.map((line: any) => {
                        if (!Array.isArray(line)) return [];
                        return line.map((coord: any) => {
                            if (Array.isArray(coord) && coord.length >= 2) {
                                return new LatLng(coord[1], coord[0]);
                            } else if (coord && typeof coord.lat === 'number' && typeof coord.lon === 'number') {
                                return new LatLng(coord.lat, coord.lon);
                            }
                            return null;
                        }).filter((c): c is LatLng => c !== null);
                    }).filter((line: any[]) => line.length >= 2);

                    if (allLinesLatLngs.length === 0) return null;

                    return allLinesLatLngs.map((lineLatLngs, lineIdx) => (
                        <Polyline
                            key={`${key}-line-${idx}-${lineIdx}`}
                            positions={lineLatLngs}
                            pathOptions={{
                                color: display.color || '#649aaa',
                                weight: display.weight ?? 2,
                                opacity: display.opacity ?? 0.8,
                            }}
                            eventHandlers={{
                                click: () => {
                                    if (properties.setObject) properties.setObject(targetEntity);
                                    if (!properties.hideOverlay) {
                                        setSelectedEntity(targetEntity);
                                    }
                                }
                            }}
                        >
                            {display.tooltip && <Tooltip>{tooltipContent(display.tooltip)}</Tooltip>}
                        </Polyline>
                    ));
                };

                if (geometry.type === 'Polygon') {
                    const poly = renderPolygon(geometry.coordinates, 0);
                    if (poly) shapeElements.push(poly);
                } else if (geometry.type === 'MultiPolygon') {
                    geometry.coordinates.forEach((polyCoords: any, i: number) => {
                        const poly = renderPolygon(polyCoords, i);
                        if (poly) shapeElements.push(poly);
                    });
                } else if (geometry.type === 'LineString') {
                    const line = renderPolyline(geometry.coordinates, 0);
                    if (line) shapeElements.push(line);
                } else if (geometry.type === 'MultiLineString') {
                    geometry.coordinates.forEach((lineCoords: any, i: number) => {
                        const line = renderPolyline(lineCoords, i);
                        if (line) shapeElements.push(line);
                    });
                }

                // Also render a marker if we have coordinates
                if (display.lat !== undefined && display.lon !== undefined) {
                    const position = new LatLng(display.lat, display.lon);
                    const poiForMarker = { ...targetEntity, poiType: display.poiType || targetEntity.poiType || display.type };
                    
                    shapeElements.push(
                        <Marker
                            key={`${key}-marker`}
                            position={position}
                            icon={poiMarker(poiForMarker)}
                            eventHandlers={{
                                click: () => {
                                    if (properties.setObject) properties.setObject(targetEntity);
                                    if (!properties.hideOverlay) {
                                        setSelectedEntity(targetEntity);
                                    }
                                }
                            }}
                        >
                            {display.tooltip && <Tooltip>{tooltipContent(display.tooltip)}</Tooltip>}
                        </Marker>
                    );
                }

                return <>{shapeElements}</>;
            }

            case 'poi': {
                if (display.lat === undefined || display.lon === undefined) return null;
                const position = new LatLng(display.lat, display.lon);
                // Ensure targetEntity has the correct poiType for the marker rendering
                const poiForMarker = { ...targetEntity, poiType: display.poiType || targetEntity.poiType, markerColor: display.markerColor };
                
                return (
                    <Marker
                        // The colour is part of the key: react-leaflet does not re-create an icon on a prop change.
                        key={display.markerColor ? `${key}-${display.markerColor}` : key}
                        position={position}
                        icon={poiMarker(poiForMarker)}
                        eventHandlers={{
                                click: () => {
                                    if (properties.setObject) properties.setObject(targetEntity);
                                    if (!properties.hideOverlay) {
                                        setSelectedEntity(targetEntity);
                                    }
                                }
                            }}
                    >
                        {display.tooltip && <Tooltip>{tooltipContent(display.tooltip)}</Tooltip>}
                    </Marker>
                );
            }

            case 'circle': {
                if (display.lat === undefined || display.lon === undefined) return null;
                return (
                    <Circle
                        key={key}
                        center={new LatLng(display.lat, display.lon)}
                        radius={display.radius || 1000}
                        pathOptions={{
                            color: display.color || '#ff0000',
                            fillColor: display.fillColor || display.color || '#ff0000',
                            fillOpacity: display.fillOpacity ?? 0.3,
                            weight: display.weight ?? 2,
                            opacity: display.opacity ?? 0.8,
                        }}
                            eventHandlers={{
                                click: () => {
                                    if (properties.setObject) properties.setObject(targetEntity);
                                    if (!properties.hideOverlay) {
                                        setSelectedEntity(targetEntity);
                                    }
                                }
                            }}
                    >
                        {display.tooltip && <Tooltip>{tooltipContent(display.tooltip)}</Tooltip>}
                    </Circle>
                );
            }

            case 'chart': {
                if (display.lat === undefined || display.lon === undefined) return null;
                // For now, render as a specialized POI or marker
                // In a full implementation, this would use a PieChart component
                return (
                    <Marker
                        key={key}
                        position={new LatLng(display.lat, display.lon)}
                        icon={poiMarker({ poiType: display.poiType })}
                            eventHandlers={{
                                click: () => {
                                    if (properties.setObject) properties.setObject(targetEntity);
                                    if (!properties.hideOverlay) {
                                        setSelectedEntity(targetEntity);
                                    }
                                }
                            }}
                    >
                        {display.tooltip && <Tooltip>{tooltipContent(display.tooltip)}</Tooltip>}
                    </Marker>
                );
            }

            default:
                return null;
        }
    };

    /**
     * `isolate` schließt Leaflets Stapelordnung in die Karte ein.
     *
     * Leaflet gibt seinen Ebenen feste Werte — die Kachelebene 400, die Bedienelemente 1000 —, und
     * die galten bisher im Stapel der ganzen Seite: Die Hülle stand zwar auf `relative`, ohne
     * `z-index` eröffnet das aber keinen eigenen Kontext. Damit trat die 400 der Karte unmittelbar
     * gegen die 50 eines aufgeklappten Menüs an und gewann; vom Kontomenü über der Heatmap war nur
     * der Kopf zu sehen.
     *
     * Mit einem eigenen Kontext gelten Leaflets Werte nur noch **innerhalb** der Karte, und die
     * Karte tritt als **ein** Element auf. `isolation` und nicht `z-index: 0`: Es eröffnet den
     * Kontext, ohne der Karte einen Rang gegenüber ihren Geschwistern zu geben — die Legenden
     * daneben liegen weiter, wo sie lagen.
     */
    const displayMap =
        () => (
            <div className="isolate" style={{ position: 'relative', width: '100%', height: properties.height || `800px` }}>
                {properties.requireModifierForScrollZoom && showZoomHint &&
                    <div style={ZOOM_HINT_STYLE}>
                        <div style={zoomHintLabelStyle(zoomHintY)}>{zoomHintLabel(t)}</div>
                    </div>}
                <MapContainer
                    ref={setMap}
                    className="mapContainer"
                    style={{ width: '100%', height: '100%', minHeight: properties.height || `800px` }}
                    bounds={properties.options?.bounds || bounds || undefined}
                    center={!properties.options?.bounds && !bounds ? center : undefined}
                    zoom={!properties.options?.bounds && !bounds ? 13 : undefined}
                    scrollWheelZoom={!properties.requireModifierForScrollZoom}
                    touchZoom={true}
                    doubleClickZoom={true}
                    dragging={true}>
                    {properties.requireModifierForScrollZoom && <ScrollZoomGate onBlockedWheel={flashZoomHint} />}
                    <TileLayer
                        attribution='&copy; <a href="https://www.mapbox.com/about/maps/">Mapbox</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url={tileUrl}
                        maxZoom={19}
                        minZoom={0}
                        tileSize={256}
                        zoomOffset={0}
                        crossOrigin={true}
                        updateWhenIdle={false}
                        updateWhenZooming={true}
                    />
                    <MapSizeHandler />
                    <TileLayerHandler />
                    <TouchHandler />
                    {bounds && <BoundsHandler bounds={bounds} />}
                    <LayersControl position="topright">
                        {/* New structured layers */}
                        {(() => {
                            const layers = (properties.layers || properties.data?.layers) as MapLayers;
                            if (!layers) return null;
                            
                            return Object.entries(layers).map(([layerId, layer]: [string, any]) => {
                                if (!layer || !layer.elements) return null;
                                const layerName = layer.name || layerId.charAt(0).toUpperCase() + layerId.slice(1);
                                
                                return (
                                    <LayersControl.Overlay key={layerId} checked={layer.visible !== false} name={layerName}>
                                        <LayerGroup>
                                            {layer.elements.map((element: MapLayerElement, index: number) => renderLayerElement(element, index, layerId))}
                                        </LayerGroup>
                                    </LayersControl.Overlay>
                                );
                            });
                        })()}

                        {!(properties.layers || properties.data?.layers) && (
                            <>
                                <LayersControl.Overlay checked name="Points of Interest">
                            <LayerGroup>
                                <>
                                    {(properties.pois || []).map((poi: any) => {
                                        // Validate POI coordinates to prevent crashes
                                        if (!poi || typeof poi.lat !== 'number' || typeof poi.lon !== 'number' || 
                                            isNaN(poi.lat) || isNaN(poi.lon)) {
                                            return null;
                                        }
                                        
                                        return (
                                        <Marker key={`${poi.lat}-${poi.lon}-${(poi as any).name || (poi as any)._id || ''}`}
                                            position={new LatLng(poi.lat, poi.lon)}
                                            opacity={1}
                                            icon={poiMarker(poi)}
                                            eventHandlers={
                                                { click: () => {
                                                    if (properties.setObject) properties.setObject(poi);
                                                    setSelectedEntity(poi);
                                                } }
                                            }>

                                            <Tooltip>{poiTooltip(poi)}</Tooltip>
                                        </Marker>
                                        );
                                    })}
                                </>
                            </LayerGroup>
                        </LayersControl.Overlay>
                        <LayersControl.Overlay checked name="Bridges">
                            <LayerGroup>
                                <>
                                    {bridges.map((bridge: any) => {
                                        // Validate bridge coordinates to prevent crashes
                                        if (!bridge || typeof bridge.lat !== 'number' || typeof bridge.lon !== 'number' || 
                                            isNaN(bridge.lat) || isNaN(bridge.lon)) {
                                            return null;
                                        }
                                        
                                        return (
                                        <Marker position={new LatLng(bridge.lat, bridge.lon)}
                                            opacity={0.3}
                                            icon={poiMarker(bridge)}
                                            eventHandlers={{ click: () => {
                                                if (properties.setObject) properties.setObject(bridge);
                                                setSelectedEntity(bridge);
                                            } }}></Marker>
                                        );
                                    })}
                                </>
                            </LayerGroup>
                        </LayersControl.Overlay>
                        <LayersControl.Overlay checked name="Buildings">
                            <LayerGroup>
                                <>
                                    {buildings.map((building: any, index: number) => {
                                        // Validate building geometry to prevent crashes
                                        if (!building || !building.geometry || !building.geometry.coordinates) {
                                            return null;
                                        }
                                        
                                        // Handle both Polygon and MultiPolygon geometry with validation
                                        let coordinates;
                                        if (building.geometry.type === 'Polygon') {
                                            if (!Array.isArray(building.geometry.coordinates) || 
                                                building.geometry.coordinates.length === 0 ||
                                                !Array.isArray(building.geometry.coordinates[0]) ||
                                                building.geometry.coordinates[0].length === 0 ||
                                                !Array.isArray(building.geometry.coordinates[0][0])) {
                                                return null;
                                            }
                                            coordinates = building.geometry.coordinates[0];
                                        } else if (building.geometry.type === 'MultiPolygon') {
                                            if (!Array.isArray(building.geometry.coordinates) || 
                                                building.geometry.coordinates.length === 0 ||
                                                !Array.isArray(building.geometry.coordinates[0])) {
                                                return null;
                                            }
                                            coordinates = building.geometry.coordinates[0].map((polygon: any) => polygon);
                                        } else {
                                            return null;
                                        }

                                        const buildingElements: any[] = [];
                                        
                                        // Validate coordinates array before processing
                                        if (!Array.isArray(coordinates)) {
                                            return null;
                                        }
                                        
                                        // Process the polygon ring as a single coordinate set
                                        if (!Array.isArray(coordinates) || coordinates.length < 3) {
                                            return null;
                                        }
                                        
                                        // Convert coordinates to LatLng format for Leaflet
                                        const latLngs = coordinates.map((coord: any) => {
                                            if (Array.isArray(coord) && coord.length >= 2) {
                                                return new LatLng(coord[1], coord[0]); // [lon, lat] -> [lat, lon]
                                            }
                                            return null;
                                        }).filter((latLng: any): latLng is LatLng => latLng !== null);
                                        
                                        if (latLngs.length < 3) {
                                            return null;
                                        }
                                            
                                            
                                        const _isSelected = isEntitySelected(building);
                                            
                                        buildingElements.push(
                                            <Polygon
                                                key={`building${index}`}
                                                    positions={latLngs}
                                                    pathOptions={{
                                                        color: '#87CEEB', // Light blue border
                                                        fillColor: '#87CEEB', // Light blue fill
                                                        weight: 3, // Thicker border for visibility
                                                        fill: true,
                                                        fillOpacity: 0.6, // More opaque for better visibility
                                                    }}
                                                    eventHandlers={{
                                                        click: () => {
                                                            if (properties.setObject) {
                                                                properties.setObject(building);
                                                            }
                                                            setSelectedEntity(building);
                                                        }
                                                    }}>
                                                    <Tooltip>
                                                        {building.name || `Building ${index + 1}`}
                                                    </Tooltip>
                                                </Polygon>
                                            );

                                        return buildingElements;
                                    })}
                                </>
                            </LayerGroup>
                        </LayersControl.Overlay>
                        <LayersControl.Overlay checked name="Areas">
                            <LayerGroup>
                                <>
                                    {areas.map((area: any, index: number) => (
                                        <Rectangle key={`areaShape${index}`}
                                            bounds={area.boundingBox}
                                            pathOptions={getStyle(options, area, heatmapMax, false, false)}
                                            eventHandlers={{
                                                mouseover: (event: any) => {
                                                    setStyle(event, getStyle(options, area, heatmapMax, true))
                                                },
                                                mouseout: (event: any) => { setStyle(event, getStyle(options, area, heatmapMax, false, false)) },
                                                click: () => {
                                                    if (properties.setObject) {
                                                        properties.setObject(area);
                                                    }

                                                    setSelectedEntity(area);
                                                }
                                            }}>
                                            {options.heatmapFieldLabel && options.heatmapField
                                                ?
                                                <Tooltip>{options.heatmapFieldLabel}: {area[options.heatmapField]}</Tooltip>
                                                :
                                                <></>}
                                    </Rectangle>
                                ))}
                            </>
                        </LayerGroup>
                    </LayersControl.Overlay>
                    <LayersControl.Overlay checked name="Gebiete">
                        <LayerGroup>
                            <>
                                {shapes.map((shape: any) => {
                                    const _isSelected = isEntitySelected(shape);
                                    return (shape.shape || []).map((coordinates: any, index: number) => {
                                        // Validate coordinates for Polygon to prevent crashes
                                        if (!Array.isArray(coordinates) || coordinates.length === 0) {
                                            return null;
                                        }
                                        
                                        // Validate each coordinate pair in the array
                                        const validCoordinates = coordinates.filter((coord: any) => 
                                            Array.isArray(coord) && coord.length >= 2 &&
                                            typeof coord[0] === 'number' && typeof coord[1] === 'number' &&
                                            !isNaN(coord[0]) && !isNaN(coord[1])
                                        );
                                        
                                        if (validCoordinates.length === 0) {
                                            return null;
                                        }
                                        
                                        return <Polygon
                                            key={`region${index}`}
                                            positions={validCoordinates}
                                            pathOptions={{
                                                color: undefined,
                                                fillColor: options.heatmap ? getShapeColorForHeatmap(shape) : '#505F90',
                                                fillOpacity: 0.2,
                                            }}
                                            eventHandlers={{
                                                click: () => {
                                                    if (properties.setObject) {
                                                        properties.setObject(shape);
                                                    }

                                                    setSelectedEntity(shape);
                                                }
                                            }}
                                        />
                                    })
                                })}
                            </>
                        </LayerGroup>
                    </LayersControl.Overlay>
                    <LayersControl.Overlay checked name="Grundstücke">
                        <LayerGroup>
                            <>
                                {parcels.map((parcel: any, index: number) => {
                                    // Validate parcel geometry to prevent crashes
                                    if (!parcel || !parcel.geometry || !parcel.geometry.coordinates) {
                                        return null;
                                    }
                                    
                                    // Handle both Polygon and MultiPolygon geometry with validation
                                    let coordinates;
                                    if (parcel.geometry.type === 'Polygon') {
                                        if (!Array.isArray(parcel.geometry.coordinates) || 
                                            parcel.geometry.coordinates.length === 0 ||
                                            !Array.isArray(parcel.geometry.coordinates[0]) ||
                                            parcel.geometry.coordinates[0].length === 0 ||
                                            !Array.isArray(parcel.geometry.coordinates[0][0])) {
                                            return null;
                                        }
                                        coordinates = [parcel.geometry.coordinates[0]];
                                    } else if (parcel.geometry.type === 'MultiPolygon') {
                                        if (!Array.isArray(parcel.geometry.coordinates) || 
                                            parcel.geometry.coordinates.length === 0 ||
                                            !Array.isArray(parcel.geometry.coordinates[0])) {
                                            return null;
                                        }
                                        coordinates = parcel.geometry.coordinates[0].map((polygon: any) => polygon);
                                    } else {
                                        coordinates = [];
                                    }

                                    const parcelElements: any[] = [];
                                    
                                    // Validate coordinates array before processing
                                    if (!Array.isArray(coordinates)) {
                                        return null;
                                    }
                                    
                                    coordinates.forEach((polygon: any, polygonIndex: number) => {
                                        // Validate polygon is an array before processing
                                        if (!Array.isArray(polygon)) {
                                            return;
                                        }
                                        
                                        // polygon is an array of coordinate arrays: [coordinateArray1, coordinateArray2, ...]
                                        
                                        // Collect all positions for this polygon
                                        const positions: any[] = [];
                                        polygon.forEach((coordinateArray: any, _arrayIndex: number) => {
                                            if (Array.isArray(coordinateArray) && coordinateArray.length >= 2) {
                                                // coordinateArray is a single [lon, lat] pair
                                                const lon = coordinateArray[0];
                                                const lat = coordinateArray[1];
                                                if (typeof lon === 'number' && typeof lat === 'number' && 
                                                    !isNaN(lon) && !isNaN(lat)) {
                                                    positions.push(new LatLng(lat, lon));
                                                }
                                            }
                                        });
                                        
                                        if (positions.length > 0) {
                                            parcelElements.push(
                                                <Polyline key={`parcelShape${index}-${polygonIndex}`}
                                                    pathOptions={getStyle(options, parcel, heatmapMax, false)}
                                                    positions={positions}
                                                    eventHandlers={{
                                                        mouseover: (event: any) => { setStyle(event, getStyle(options, parcel, heatmapMax, true)) },
                                                        mouseout: (event: any) => { setStyle(event, getStyle(options, parcel, heatmapMax, false)) },
                                                        click: () => {
                                                            if (properties.setObject) {
                                                                properties.setObject(parcel);
                                                            }

                                                            setSelectedEntity(parcel);
                                                        }
                                                    }}>
                                                    {options.heatmapFieldLabel && options.heatmapField
                                                        ?
                                                        <Tooltip>{options.heatmapFieldLabel}: {parcel[options.heatmapField]}</Tooltip>
                                                        :
                                                        <></>
                                                    }
                                                </Polyline>
                                            );
                                        }
                                    });

                                    return parcelElements;
                                })}
                            </>
                        </LayerGroup>
                    </LayersControl.Overlay>
                    <LayersControl.Overlay checked name="Circles">
                        <LayerGroup>
                            <>
                                {circles.map((circle: any, index: number) => {
                                    // Validate circle coordinates to prevent crashes
                                    if (!circle || typeof circle.lat !== 'number' || typeof circle.lon !== 'number' || 
                                        isNaN(circle.lat) || isNaN(circle.lon)) {
                                        return null;
                                    }
                                    
                                    // Get radius in km, default to 1km if not specified
                                    const radiusKm = circle.radius || circle.radiusKm || 1;
                                    
                                    // Convert radius from km to meters for Leaflet Circle
                                    const radiusMeters = radiusKm * 1000;
                                    
                                    return (
                                        <Circle
                                            key={`circle${index}`}
                                            center={new LatLng(circle.lat, circle.lon)}
                                            radius={radiusMeters}
                                            pathOptions={{
                                                color: circle.color || '#ff0000',
                                                fillColor: circle.fillColor || circle.color || '#ff0000',
                                                fillOpacity: circle.fillOpacity || 0.3,
                                                weight: circle.weight || 2,
                                                opacity: circle.opacity || 0.8,
                                            }}
                                            eventHandlers={{
                                                click: () => {
                                                    if (properties.setObject) {
                                                        properties.setObject(circle);
                                                    }
                                                    setSelectedEntity(circle);
                                                }
                                            }}>
                                            <Tooltip>
                                                {circle.tooltip || circle.name || `Circle (${radiusKm}km radius)`}
                                            </Tooltip>
                                        </Circle>
                                    );
                                })}
                            </>
                        </LayerGroup>
                    </LayersControl.Overlay>
                            </>
                        )}
                </LayersControl>
                {properties.showMiniMap
                    ?
                    <MinimapControl position="topright" zoom="1" />
                    :
                    <></>}
                {options.heatmapField || options.heatmap
                    ?
                    <div className="legend">
                        <div className="title">{properties.options?.legend?.title || 'Legend'}</div>
                        <div className="marginTopM" style={{
                            display: 'grid',
                            gridTemplateColumns: options.heatmap?.categories && options.heatmap.categories.length > 8 ? `repeat(${Math.ceil(options.heatmap.categories.length / 8)}, 1fr)` : '1fr',
                            gap: '4px',
                            alignItems: 'start',
                            gridAutoFlow: 'row',
                            gridTemplateRows: 'repeat(8, auto)'
                        }}>
                            {options.heatmap?.categories ?
                                options.heatmap.categories.map((category: any, index: number) => {
                                    return <div key={`category${index}`} className="displayFlex alignItemsCenter">
                                        <div className="width20 height20" style={{ backgroundColor: category.color }}></div>
                                        <div className="marginLeftS">{category.label}</div>
                                    </div>
                                })
                                :
                                [...Array(5)].map((entry, index) => {
                                    return <div key={`category${index}`} className="displayFlex alignItemsCenter">
                                        <div className="width20 height20" style={{ backgroundColor: percentToColor(index / 5 * 100).toString() }}></div>
                                        <div className="marginLeftS">&le; {((options.heatmapField?.heatmapMax || heatmapMax) / 5 * (index + 1)).toFixed(0)}</div>
                                    </div>
                                })}
                        </div>
                    </div>
                    :
                    <></>
                }
                {options.categoryField
                    ?
                    <div className="legend">
                        <div className="title">{properties.options?.legend?.title || 'Legende'}</div>
                        <div className="marginTopM" style={{
                            display: 'grid',
                            gridTemplateColumns: options.categoryField.values.length > 8 ? `repeat(${Math.ceil(options.categoryField.values.length / 8)}, 1fr)` : '1fr',
                            gap: '4px',
                            alignItems: 'start',
                            gridAutoFlow: 'row',
                            gridTemplateRows: 'repeat(8, auto)'
                        }}>
                            {options.categoryField.values.map((value: any, index: number) => {
                                return <div key={`categoryField${index}`} className="displayFlex alignItemsCenter">
                                    <div className="width20 height20" style={{ backgroundColor: value.color }}></div>
                                    <div className="marginLeftS">{value.label}</div>
                                </div>
                            })}
                        </div>
                    </div>
                    :
                    <></>
                }
                {pieChartData
                    ?
                    <ClusterWithSuperCluster
                        data={pieChartData}
                        options={{
                            ...(properties.options?.pieChart || {}),
                            clusterPieChart: properties.options?.clusterPieChart,
                        }}
                    />
                    :
                    <></>
                }
                <SelectionHighlight entity={selectedEntity} />
            </MapContainer>
        </div>
    );

    return (
        <div>
            {displayMap()}
        </div>
    );
});

export default Map;
